import { RouterStore } from "@superwf/mobx-react-router";
import { makeAutoObservable } from "mobx";
import { clearPersistedStore, makePersistable } from "mobx-persist-store";
import { StoreDTO, UserRoleName } from "models";
import { MyStorePaths } from "routes";
import { Api, LoginPayload, PostLoginNavigation, RegisterPayload, User } from "services";

const POST_LOGIN_NAV_KEY = "postLoginNav";

function resolvePostLoginNav(explicit?: PostLoginNavigation): PostLoginNavigation | undefined {
  if (explicit?.from) return explicit;
  try {
    const raw = sessionStorage.getItem(POST_LOGIN_NAV_KEY);
    if (!raw) return undefined;
    return JSON.parse(raw) as PostLoginNavigation;
  } catch {
    return undefined;
  }
}

function clearStoredPostLoginNav(): void {
  try {
    sessionStorage.removeItem(POST_LOGIN_NAV_KEY);
  } catch {
    /* ignore */
  }
}

export class UserStore {
  private userApi: Api["userApi"];
  private storeApi: Api["storeApi"];
  private routes: RouterStore;
  private rootStore: any;

  constructor(rootStore: any) {
    this.userApi = rootStore.generalStore.api.userApi;
    this.storeApi = rootStore.generalStore.api.storeApi;
    this.routes = rootStore.routingStore;
    this.rootStore = rootStore;

    makeAutoObservable(this);
    makePersistable(this, {
      name: "UserStore",
      properties: ["assignedStore"],
      storage: window.localStorage,
    });
  }

  // OBSERVABLE
  public assignedStore: StoreDTO = null;
  public managementStoreId: number = null;
  public viewerMenuVisible: boolean = false;

  // ACTION
  private setAssignedStore = (store: StoreDTO): void => {
    this.assignedStore = store;
  };

  public setViewerMenuVisible = (visible: boolean) => {
    this.viewerMenuVisible = visible;
  };

  public setManagementStoreId = (storeId?: number | string): void => {
    if (!storeId) {
      this.managementStoreId = null;
      return;
    }

    const parsedStoreId = Number(storeId);
    this.managementStoreId = Number.isNaN(parsedStoreId) ? null : parsedStoreId;
  };

  // METHODS
  public login = async (payload: LoginPayload, postLoginNav?: PostLoginNavigation) => {
    const authPayload = await this.userApi.login(payload);
    if (!authPayload) return;
    User.authenticate(authPayload);
    const isTempUser = JSON.parse(localStorage.getItem("isTempUser"));
    if (isTempUser) {
      localStorage.removeItem("isTempUser");
    }
    const nav = resolvePostLoginNav(postLoginNav);
    clearStoredPostLoginNav();
    if (nav?.from) {
      this.routes.push(nav.from);
      return authPayload?.userDetails?.id;
    }
    let redirectRoute = "/";
    if (UserRoleName.VIEWER === authPayload?.userDetails?.role.name) {
      redirectRoute = MyStorePaths.STORES;
    } else {
      await this.loadAssignedStores();
      if (User.hasAssignedStores) {
        this.setAssignedStore(User.firstAssignedStore);
        redirectRoute = MyStorePaths.ROOT;
      }
    }
    this.routes.push(redirectRoute);
    return authPayload?.userDetails?.id;
  };

  public register = async (payload: RegisterPayload, postLoginNav?: PostLoginNavigation) => {
    const authPayload = await this.userApi.register(payload);
    if (!authPayload) return;
    User.authenticate(authPayload);
    const isTempUser = JSON.parse(localStorage.getItem("isTempUser"));
    if (isTempUser) {
      localStorage.removeItem("isTempUser");
    }
    const nav = resolvePostLoginNav(postLoginNav);
    clearStoredPostLoginNav();
    if (nav?.from) {
      this.routes.push(nav.from);
      return;
    }
    this.routes.push("/");
  };

  public socialLogin = async (provider: string, token: string, postLoginNav?: PostLoginNavigation) => {
    const authPayload = await this.userApi.socialLogin({ provider, token });
    if (!authPayload) return;
    User.authenticate(authPayload);
    const isTempUser = JSON.parse(localStorage.getItem("isTempUser"));
    if (isTempUser) {
      localStorage.removeItem("isTempUser");
    }
    const nav = resolvePostLoginNav(postLoginNav);
    clearStoredPostLoginNav();
    if (nav?.from) {
      this.routes.push(nav.from);
      return authPayload?.userDetails?.id;
    }
    let redirectRoute = "/";
    if (UserRoleName.VIEWER === authPayload?.userDetails?.role?.name) {
      redirectRoute = MyStorePaths.STORES;
    } else {
      await this.loadAssignedStores();
      if (User.hasAssignedStores) {
        this.setAssignedStore(User.firstAssignedStore);
        redirectRoute = MyStorePaths.ROOT;
      }
    }
    this.routes.push(redirectRoute);
    return authPayload?.userDetails?.id;
  };

  public loginTemp = async (buyNow?: boolean) => {
    const loginTempAuth = await this.userApi.getUserTemp();
    if (!loginTempAuth) return;
    User.authenticate(loginTempAuth);
    localStorage.setItem("isTempUser", "true");
  };

  public clearStoredDate = async () => {
    await clearPersistedStore(this).then(() => {
      window.localStorage.clear();
      this.setAssignedStore(null);
      this.setManagementStoreId(null);
    });
  };

  private loadAssignedStores = async (): Promise<void> => {
    // const assignedStores = await this.storeApi.getStoreByUser(User.emailAddress)
    const assignedStores = await this.storeApi.getStoreByUser(
      {
        silentProcess: true, // silenetProcess is disable the loader
      },
    ); // It's uncomment because that's working with exact flow

    // let assignedStores : any = await this.rootStore.storesPublic.getAllStores({ resetPageNumber: false })
    if (!assignedStores) return;
    User.setAssignedStores(assignedStores);
  };

  public updateAssignedStore = async (): Promise<void> => {
    const store = await this.rootStore.storesMainStore.getStoreById(
      this.assignedStoreId,
    );
    if (!store) return;
    this.setAssignedStore(store);
  };
  // COMPUTED
  public get assignedStoreId(): number {
    return this.assignedStore?.id;
  }

  public get effectiveStoreId(): number {
    return this.managementStoreId ?? this.assignedStoreId;
  }
}
